import { useState, useCallback, useEffect, useTransition } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Box, Typography, Alert, AlertTitle, Paper, CircularProgress } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Container } from '~/components/ui';
import { BarcodeInput } from '~/components/BarcodeInput';
import { PhoneInput } from '~/components/PhoneInput';
import { DateInput } from '~/components/DateInput';
import {
  RegistrationRequestSchema,
  PHONE_REGEX,
} from '../../lib/customer-schema';
import { createCustomer } from '~/lib/api/api-client';
import { useDevice } from '~/lib/auth/device-context';
import { queue } from '~/components/SnackbarQueue';

// Field-specific error messages for the two most common failure modes
// (Task B). Keeps the user informed instead of swallowing the cause.
const FIELD_ERRORS: Record<string, string> = {
  DUPLICATE_BARCODE: "هذا الباركود مسجل مسبقاً. يرجى مسح الباركود ضوئياً أو البحث مرة أخرى.",
  DUPLICATE_PHONE: "رقم الهاتف هذا مسجل مسبقاً. هل العميل موجود في قائمة البحث؟",
  INVALID_PHONE: "رقم الهاتف يجب أن يكون 11 رقماً ويبدأ بـ 01 (مثال: 01012345678).",
  VALIDATION_ERROR: "بيانات غير صحيحة. يرجى مراجعة الباركود ورقم الهاتف.",
  SERVER_ERROR: "حدث خطأ في الخادم. يرجى المحاولة مرة أخرى.",
};

export default function Register() {
  const [searchParams] = useSearchParams();
  const suggestedBarcode = searchParams.get("barcode") || "";
  const suggestedPhone = searchParams.get("phone") || "";
  const navigate = useNavigate();
  const { deviceToken } = useDevice();

  const [barcode, setBarcode] = useState(suggestedBarcode);
  const [phone, setPhone] = useState(suggestedPhone);
  const [name, setName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [barcodeError, setBarcodeError] = useState("");
  const [barcodeValid, setBarcodeValid] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [error, setError] = useState("");
  const [isPending, startTransition] = useTransition();
  const [redirectTimer, setRedirectTimer] = useState<number | null>(null);
  const [registeredCustomer, setRegisteredCustomer] = useState<any>(null);

  // Validate pre-filled barcode on mount using the same Zod schema the
  // server uses (Task B: single source of truth).
  useEffect(() => {
    if (suggestedBarcode) {
      const result = RegistrationRequestSchema.shape.barcode.safeParse(suggestedBarcode);
      setBarcodeValid(result.success);
      if (!result.success) {
        setBarcodeError("الرجاء التحقق من البيانات المدخلة");
      }
    }
  }, [suggestedBarcode]);

  // Validate pre-filled phone on mount.
  useEffect(() => {
    if (suggestedPhone && !PHONE_REGEX.test(suggestedPhone)) {
      setPhoneError(FIELD_ERRORS.INVALID_PHONE);
    }
  }, [suggestedPhone]);

  // Auto-redirect countdown after successful registration
  useEffect(() => {
    if (redirectTimer === null) return;

    if (redirectTimer <= 0) {
      if (registeredCustomer) {
        navigate(`/?barcode=${registeredCustomer.barcode}`);
      }
      return;
    }

    const timer = setTimeout(() => {
      setRedirectTimer(redirectTimer - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [redirectTimer, registeredCustomer, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBarcodeError("");
    setPhoneError("");

    if (!barcode) {
      setBarcodeError("الرجاء التحقق من البيانات المدخلة");
      return;
    }

    // Pre-validate locally using the shared schema. A request that
    // fails here never reaches the server.
    const localCheck = RegistrationRequestSchema.safeParse({
      barcode,
      name: name.trim(),
      phone,
      date_of_birth: birthdate || undefined,
    });
    if (!localCheck.success) {
      for (const issue of localCheck.error.issues) {
        const field = issue.path[0];
        if (field === "barcode") setBarcodeError(issue.message);
        else if (field === "phone") setPhoneError(issue.message);
        else setError(issue.message);
      }
      return;
    }

    if (!deviceToken) {
      setError("الجهاز غير مسجل");
      return;
    }

    // useTransition keeps the form inputs responsive while the submit
    // is in flight (Task B). React's transition callbacks must be sync,
    // so we kick off the async work without awaiting it.
    startTransition(() => {
      void (async () => {
        try {
          const result = await createCustomer({
            barcode: localCheck.data.barcode,
            name: localCheck.data.name,
            phone: localCheck.data.phone,
            date_of_birth: localCheck.data.date_of_birth,
            deviceToken,
          });

          if (result.success) {
            queue.notify({
              title: '✓ تم تسجيل العميل بنجاح',
              body: `تم تسجيل ${result.customer.name}`,
              timeout: 3000,
            });
            setRegisteredCustomer(result.customer);
            setRedirectTimer(10);
          } else {
            // Map server's typed error codes (DUPLICATE_BARCODE, INVALID_PHONE, …)
            // to field-level messages.
            const code = (result as any).code as string | undefined;
            if (code === "DUPLICATE_BARCODE") {
              setBarcodeError(FIELD_ERRORS.DUPLICATE_BARCODE);
            } else if (code === "DUPLICATE_PHONE" || code === "INVALID_PHONE") {
              setPhoneError(FIELD_ERRORS[code] || FIELD_ERRORS.INVALID_PHONE);
            } else {
              setError(
                (code && FIELD_ERRORS[code]) ||
                  result.error ||
                  "حدث خطأ أثناء التسجيل",
              );
            }
          }
        } catch (err: any) {
          setError(err.message || "حدث خطأ أثناء التسجيل");
        }
      })();
    });
  };

  const handleBarcodeChange = useCallback((value: string) => {
    setBarcode(value);
    if (!value) {
      setBarcodeValid(false);
      setBarcodeError("");
      return;
    }
    const result = RegistrationRequestSchema.shape.barcode.safeParse(value);
    setBarcodeValid(result.success);
    if (!result.success) {
      setBarcodeError("الرجاء التحقق من البيانات المدخلة");
    } else {
      setBarcodeError("");
    }
  }, []);

  const handlePhoneChange = useCallback((value: string) => {
    setPhone(value);
    if (!value) {
      setPhoneError("");
      return;
    }
    if (!PHONE_REGEX.test(value)) {
      setPhoneError(FIELD_ERRORS.INVALID_PHONE);
    } else {
      setPhoneError("");
    }
  }, []);

  const handleNameChange = useCallback((e: any) => setName(e.target.value), []);

  // Submit button is enabled only when the shared Zod schema says the
  // three required fields are valid. This is the single source of truth
  // for "ready to submit" (Task B).
  const readyToSubmit =
    !!barcode &&
    barcodeValid &&
    name.trim().length > 0 &&
    PHONE_REGEX.test(phone) &&
    !isPending;

  return (
    <Container size="xl" padding>
      <Box sx={{ py: 2 }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowForward />}
          variant="text"
        >
          رجوع
        </Button>
      </Box>

      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: 3,
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
          تسجيل عميل جديد
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              الباركود <span style={{ color: 'error.main' }}>*</span>
            </Typography>
            <BarcodeInput
              value={barcode}
              onChange={handleBarcodeChange}
              disabled={isPending}
              error={!!barcodeError}
              helperText={barcodeError}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              الاسم <span style={{ color: 'error.main' }}>*</span>
            </Typography>
            <TextField
              value={name}
              onChange={handleNameChange}
              name="name"
              autoComplete="name"
              placeholder="أحمد محمد"
              required
              disabled={isPending}
              fullWidth
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              رقم الهاتف <span style={{ color: 'error.main' }}>*</span>
            </Typography>
            {suggestedPhone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                رقم الهاتف المسجل مسبقاً
              </Typography>
            )}
            <PhoneInput
              value={phone}
              onChange={handlePhoneChange}
              disabled={isPending}
              error={!!phoneError}
              helperText={phoneError}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              تاريخ الميلاد
            </Typography>
            <DateInput
              value={birthdate}
              onChange={setBirthdate}
              disabled={isPending}
            />
          </Box>

          {error && (
            <Alert severity="error">
              <AlertTitle>خطأ</AlertTitle>
              {error}
            </Alert>
          )}

          {registeredCustomer && redirectTimer !== null && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>✓ تم التسجيل بنجاح</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                تم تسجيل {registeredCustomer.name} بنجاح
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  الانتقال لصفحة المعاملات بعد {redirectTimer} ثانية...
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={() => navigate(`/?barcode=${registeredCustomer.barcode}`)}
                >
                  بدء معاملة الآن
                </Button>
              </Box>
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!readyToSubmit}
            fullWidth
            sx={{ height: 56, mt: 2 }}
            startIcon={isPending ? <CircularProgress size={20} /> : null}
          >
            {isPending ? 'جاري التسجيل...' : 'تسجيل العميل'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
